import React, {useEffect, useState} from "react";
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  Stack,
  Chip,
} from "@mui/material";
import UsersCRUD from "./Users";
import PeopleIcon from "@mui/icons-material/People";
import SettingsIcon from "@mui/icons-material/Settings";
import NotesIcon from "@mui/icons-material/Notes";
import KeyIcon from "@mui/icons-material/Key";
import LockIcon from "@mui/icons-material/Lock";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ReleaseNotesCRUD from "./ReleaseNotes";
import AuthTokensCRUD from "./AuthTokens";
import BucketAccessListsCRUD from "./BucketAccessLists";
import TokenBucketAccessCRUD from "./TokenBucketAccess";
import {PORTAL_GRADIENT, PORTAL_PRIMARY, PORTAL_SOFT_BG} from "./AdminStyles";

const TABS = [
  {
    key: "release-notes",
    label: "Release Notes",
    icon: <NotesIcon fontSize="small"/>,
    description: "Manage release notes for your buckets",
    Component: ReleaseNotesCRUD,
  },
  {
    key: "bucket-access-lists",
    label: "Bucket Access Lists",
    icon: <KeyIcon fontSize="small"/>,
    description: "Control access permissions for storage buckets",
    Component: BucketAccessListsCRUD,
  },
  {
    key: "auth-tokens",
    label: "Auth Tokens",
    icon: <LockIcon fontSize="small"/>,
    description: "Create, rotate, and revoke authentication tokens",
    Component: AuthTokensCRUD,
  },
  {
    key: "token-bucket-access",
    label: "Bucket Access Roles",
    icon: <KeyIcon fontSize="small"/>,
    description: "Map buckets to authentication tokens",
    Component: TokenBucketAccessCRUD,
  },
  {
    key: "users",
    label: "Users",
    icon: <PeopleIcon fontSize="small"/>,
    description: "View users",
    Component: UsersCRUD,
  },
];

function TabPanel({children, value, index}) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{pt: 1}}>{children}</Box>}
    </div>
  );
}

export default function AdminHome({user}) {
  const isAdmin = !!user?.is_qli_admin
  const isTeamAdmin = !!user?.is_team_admin
  const isAppAdmin = !!user?.is_app_admin
  
  const getInitialTab = () => {
    const stored = localStorage.getItem("admin-home-tab");
    const idx = TABS.findIndex((t) => t.key === stored);
    return idx >= 0 ? idx : 0;
  };
  
  const [activeTab, setActiveTab] = useState(getInitialTab);
  
  useEffect(() => {
    document.title = "QLI Artifacts | Admin";
  }, []);
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    localStorage.setItem("admin-home-tab", TABS[newValue].key);
  };
  
  return (
    <Box sx={{maxWidth: 1400, mx: "auto", p: {xs: 2, md: 3}}}>
      {/* Admin Header */}
      <Paper
        elevation={0}
        sx={{
          p: 1.5,
          mb: 3,
          borderRadius: 3,
          background: PORTAL_GRADIENT,
          color: "white",
        }}
      >
        <Stack
          direction={{xs: "column", sm: "row"}}
          justifyContent="space-between"
          alignItems={{xs: "flex-start", sm: "center"}}
          spacing={2}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <AdminPanelSettingsIcon sx={{fontSize: 40}}/>
            <Box>
              <Typography variant="h6" fontWeight={700} sx={{mb: 0.5}}>
                Admin Section
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip
              icon={<SettingsIcon sx={{color: "white !important"}}/>}
              label={isAdmin ? "QLI Admin" : isTeamAdmin ? "Team Admin" : "Read-Only"}
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.18)",
                color: "white",
                fontWeight: 600,
                border: "1px solid rgba(255, 255, 255, 0.3)",
              }}
            />
            {user?.username && (
              <Chip
                label={user.username}
                sx={{
                  backgroundColor: "rgba(255, 255, 255, 0.15)",
                  color: "white",
                  fontWeight: 500,
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                }}
              />
            )}
          </Stack>
        </Stack>
      </Paper>
      
      {/* Tabs */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: 2,
          border: "1px solid #e0e0e0",
          overflow: "hidden",
        }}
      >
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            "& .MuiTabs-indicator": {
              height: 3,
              borderRadius: "3px 3px 0 0",
              backgroundColor: PORTAL_PRIMARY,
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.95rem",
              minHeight: 56,
              px: 3,
              color: "#5a6b7d",
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: PORTAL_SOFT_BG,
                color: PORTAL_PRIMARY,
              },
              "&.Mui-selected": {
                color: "#2d3e50",
                backgroundColor: PORTAL_SOFT_BG,
              },
            },
          }}
        >
          {TABS.map((tab) => (
            <Tab
              key={tab.key}
              icon={tab.icon}
              iconPosition="start"
              label={tab.label}
            />
          ))}
        </Tabs>
      </Paper>
      
      {/* Tab Panels */}
      {TABS.map((tab, idx) => {
        const Component = tab.Component;
        return (
          <TabPanel key={tab.key} value={activeTab} index={idx}>
            <Component admin={isAdmin} userId={user?.id} appAdmin={isAppAdmin} teamAdmin={isTeamAdmin}/>
          </TabPanel>
        );
      })}
    </Box>
  );
}
